// Explore-stats page: Outcomes ledger (#216).
// Per-week 100%-stacked bar of `items.status` outcome buckets across the
// captured live_minutes corpus. Buckets normalised via the #198 workaround
// (`_normalise_status` + `_outcome_bucket` on the backend) — trailing-space
// and typo duplicates de-dupe at query time.
//
// Supporting slices: agreed-vs-negatived ratio per day, document-tabling
// cadence per chamber-day, most-postponed-items leaderboard, portfolio
// outcome mix (top 10 active portfolios).

import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack, Alert,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatStackedBar, HundredPctStacked } from './charts';

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function OutcomesLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('outcomes-ledger');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const buckets = c.buckets || [];
  const totals = c.bucket_totals || [];
  const cov = c.coverage || {};

  // Per-week share rows for the flagship 100%-stacked bar. Each row already
  // has the bucket counts; HundredPctStacked normalises them to % at render.
  const perWeekRows = useMemo(() => (
    (c.per_week || []).map((r) => ({
      label: r.week,
      ...buckets.reduce((acc, b) => ({ ...acc, [b]: r[b] || 0 }), {}),
    }))
  ), [c.per_week, buckets]);

  // Per-portfolio share rows for the secondary stacked bar.
  const portfolioRows = useMemo(() => (
    (c.portfolio_mix || []).map((r) => ({
      label: r.portfolio,
      ...buckets.reduce((acc, b) => ({ ...acc, [b]: r[b] || 0 }), {}),
    }))
  ), [c.portfolio_mix, buckets]);

  return (
    <ExploreStatsPage
      title="Outcomes ledger"
      description="What happened on the floor — every business item's outcome captured in `live_minutes.items.status`, mapped to a curated outcome bucket (Agreed / Negatived / Postponed / Document tabled / Bill passed / Interrupted / In progress / etc.). Status keywords pass through `_normalise_status` (#198 workaround) so trailing-space + typo duplicates ('Agreed to' vs 'Agreed to ') don't split categories."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="outcomes-ledger"
    >
      {data && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            {cov.with_status} of {cov.items} items have a status keyword (
            {(100 - cov.missing_pct).toFixed(1)}% coverage). Items with no
            status keyword are excluded from the stacked view.
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {totals.slice(0, 4).map((b) => (
              <Grid item xs={6} md={3} key={b.bucket}>
                <StatCard label={b.bucket} value={b.count}
                          sub="captured outcomes" />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Outcome share per sitting week
                </Typography>
                <HundredPctStacked rows={perWeekRows} categories={buckets}
                                   labelKey="label" leftMargin={100}
                                   height={Math.max(280, 40 * perWeekRows.length + 80)} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Overall outcome ranking
                </Typography>
                <StatBar data={totals} xKey="bucket" yKey="count"
                         layout="vertical" height={360} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Agreed vs negatived per chamber-day (% agreed)
                </Typography>
                <StatBar data={(c.ratio_agreed_neg || []).map((r) => ({
                  date: r.date, pct: r.ratio_pct,
                }))} xKey="date" yKey="pct" height={360} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Document-tabling cadence per chamber-day
                </Typography>
                <StatBar data={c.document_cadence || []} xKey="date"
                         yKey="docs" height={360} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Most-postponed items
                </Typography>
                {(c.most_postponed || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No postponements captured in this window.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {(c.most_postponed || []).map((p, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 2,
                                         borderBottom: '1px solid',
                                         borderColor: 'divider', pb: 1 }}>
                        <Box sx={{ flex: 1, fontSize: 13 }}>{p.item}</Box>
                        <Box sx={{ minWidth: 130, fontSize: 12,
                                    color: 'text.secondary' }}>
                          {p.chamber}
                        </Box>
                        <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                          ×{p.count}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Outcome mix by portfolio (counts)
                </Typography>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}>
                  Bill items only — portfolio resolved from the bills index.
                  Top 10 portfolios by activity in the captured window.
                </Typography>
                {portfolioRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No portfolio-tagged bill items in the captured window.
                  </Typography>
                ) : (
                  <StatStackedBar data={portfolioRows} xKey="label"
                                  keys={buckets} layout="vertical"
                                  height={Math.max(280, 40 * portfolioRows.length + 80)} />
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
